import './style.scss';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Draggable } from '../Draggable';
import { Resizable } from '../Resizable';
import { Rotateable } from '../Rotateable';
import { Deleteable } from '../Deleteable';
import { Scrubber } from '../Scrubber';
import { Animated } from 'react-animated-css';

function between(x, min, max) {
  return x >= min && x <= max;
}

const noop = () => {};

const getElementObject = (element, isEditable = true, isVisible) => {
  switch (element.type) {
    case 'image':
      return (
        <Animated
          style={{
            height: '100%',
            width: '100%',
          }}
          animateOnMount={false}
          animationIn={element.animate.in}
          animationOut={element.animate.out}
          isVisible={isVisible}>
          <img
            className={`${isEditable ? 'editable' : ''}`}
            alt=''
            src={element.url}
            style={{ height: `100%`, width: `100%`, objectFit: `contain`, transform: `rotate(${isEditable ? 0 : element.rotate}deg)` }}
          />
        </Animated>
      );
    default:
      return <span />;
  }
};

export const VideoPlayer = ({
  isEditMode,
  src,
  onUpdateTotalDuration = noop,
  onUpdateCurrentTime = noop,
  onUpdateElement = noop,
  onDeleteElement = noop,
  config = { elements: [] },
}) => {
  const [videoPlayerContainerSize, setVideoPlayerContainerSize] = useState({ height: '100%', width: '100%', position: 'relative' });
  const [videoObjectSize, setVideObjectSize] = useState();
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoControlsVisible, setIsVideoControlsVisible] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const videoObjectRef = useRef();
  const videoPlayerContainerRef = useRef();
  const scrubberRef = useRef();

  const onTogglePlay = () => {
    if (videoObjectRef.current) {
      if (videoObjectRef.current.playing) {
        videoObjectRef.current.pause();
        setIsPlaying(false);
      } else {
        videoObjectRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const onToggleFullScreen = () => {
    if (!isFullScreen) {
      if (videoPlayerContainerRef.current.requestFullscreen) {
        videoPlayerContainerRef.current.requestFullscreen();
      } else if (videoPlayerContainerRef.current.mozRequestFullScreen) {
        /* Firefox */
        videoPlayerContainerRef.current.mozRequestFullScreen();
      } else if (videoPlayerContainerRef.current.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        videoPlayerContainerRef.current.webkitRequestFullscreen();
      } else if (videoPlayerContainerRef.current.msRequestFullscreen) {
        /* IE/Edge */
        videoPlayerContainerRef.current.msRequestFullscreen();
      } else {
        // Fullscreen API not supported, fallback !
        setVideoPlayerContainerSize({
          position: 'fixed',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: '1000',
        });
        requestAnimationFrame(() => {
          requestAnimationFrame(drawOverlay);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        /* Firefox */
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE/Edge */
        document.msExitFullscreen();
      } else {
        //Fullscreen API not supported, fallback !
        setVideoPlayerContainerSize({
          position: 'relative',
          height: '100%',
          width: '100%',
        });
        requestAnimationFrame(() => {
          requestAnimationFrame(drawOverlay);
        });
      }
    }

    setIsFullScreen(!isFullScreen);
  };

  const drawOverlay = () => {
    const videoPlayerContainerRect = videoPlayerContainerRef.current.getBoundingClientRect();

    const originalVideoHeight = videoObjectRef.current.videoHeight;
    const originalVideoWidth = videoObjectRef.current.videoWidth;

    const scale1 = originalVideoWidth / videoPlayerContainerRect.width;
    const scale2 = originalVideoHeight / videoPlayerContainerRect.height;
    if (scale1 > scale2) {
      setVideObjectSize({
        height: originalVideoHeight / scale1,
        width: videoPlayerContainerRect.width,
        left: 0,
        top: (videoPlayerContainerRect.height - originalVideoHeight / scale1) / 2,
      });
    } else {
      setVideObjectSize({
        height: videoPlayerContainerRect.height,
        width: originalVideoWidth / scale2,
        left: (videoPlayerContainerRect.width - originalVideoWidth / scale2) / 2,
        top: 0,
      });
    }
  };

  const onUpdateTime = ({ value }) => {
    videoObjectRef.current.currentTime = value;
  };

  const updateTime = (event) => {
    const currentTime = videoObjectRef.current.currentTime;
    scrubberRef.current.setValue(currentTime);
    setCurrentTime(currentTime);
    onUpdateCurrentTime({ currentTime });
  };

  const updateTotalDuration = (event) => {
    const totalDuration = videoObjectRef.current.duration;
    setTotalDuration(totalDuration);
    onUpdateTotalDuration({ totalDuration });
  };

  const onKeyDown = (event) => {
    if (event && event.keyCode == 32) {
      onTogglePlay();
    }
  };

  useLayoutEffect(() => {
    let isTimeoutCallbackSet = false;
    const onShowVideoControls = () => {
      if (isTimeoutCallbackSet) {
        isTimeoutCallbackSet = false;
      }
      setIsVideoControlsVisible(true);
    };
    const onHideVideoControls = () => {
      isTimeoutCallbackSet = true;
      setTimeout(() => {
        if (isTimeoutCallbackSet) {
          setIsVideoControlsVisible(false);
          isTimeoutCallbackSet = false;
        }
      }, 2000);
    };
    //? Register Events
    window.addEventListener('resize', drawOverlay);

    videoObjectRef.current.addEventListener('loadedmetadata', drawOverlay);
    videoObjectRef.current.addEventListener('loadedmetadata', updateTotalDuration);
    videoObjectRef.current.addEventListener('timeupdate', updateTime);

    videoPlayerContainerRef.current.addEventListener('mouseenter', onShowVideoControls);
    videoPlayerContainerRef.current.addEventListener('mouseleave', onHideVideoControls);

    document.addEventListener('keyup', onKeyDown);

    document.addEventListener('fullscreenchange', drawOverlay);
    document.addEventListener('webkitfullscreenchange', drawOverlay);
    document.addEventListener('mozfullscreenchange', drawOverlay);
    document.addEventListener('msfullscreenchange', drawOverlay);

    window.addEventListener('orientationchange', drawOverlay);

    return () => {
      //? Deregister Events
      window.removeEventListener('resize', drawOverlay);

      document.removeEventListener('keyup', onKeyDown);

      document.removeEventListener('fullscreenchange', drawOverlay);
      document.removeEventListener('webkitfullscreenchange', drawOverlay);
      document.removeEventListener('mozfullscreenchange', drawOverlay);
      document.removeEventListener('msfullscreenchange', drawOverlay);

      window.removeEventListener('orientationchange', drawOverlay);
    };
  }, [src]);

  return (
    <div className='video-player-container' ref={videoPlayerContainerRef} style={videoPlayerContainerSize}>
      <video src={src} ref={videoObjectRef} controls={false} className='video-object' playsInline={true} />
      {videoObjectSize && (
        <div
          className='overlay-container'
          style={{
            height: `${videoObjectSize.height}px`,
            width: `${videoObjectSize.width}px`,
            left: `${videoObjectSize.left}px`,
            top: `${videoObjectSize.top}px`,
          }}>
          {config.elements.map((element) => {
            const isVisible = between(currentTime, element.at - 0.2, element.at + element.duration);
            if (isEditMode) {
              return (
                <div style={{ opacity: isVisible ? 1 : 0 }} key={element.id}>
                  <Draggable
                    onDragEnd={({ top, left }) => {
                      onUpdateElement({
                        ...element,
                        position: {
                          x: left / videoObjectSize.width,
                          y: top / videoObjectSize.height,
                        },
                      });
                    }}
                    id={element.id}
                    top={element.position.y * videoObjectSize.height}
                    left={element.position.x * videoObjectSize.width}
                    height={element.size.height * videoObjectSize.height}
                    width={element.size.width * videoObjectSize.width}>
                    <Rotateable
                      element={element}
                      isEditable={true}
                      onRotate={({ angle: rotate }) => {
                        onUpdateElement({
                          ...element,
                          rotate,
                        });
                      }}>
                      <Deleteable onDelete={onDeleteElement.bind(undefined, element)}>
                        <Resizable
                          isEditable={true}
                          onResize={({ height, width }) => {
                            onUpdateElement({
                              ...element,
                              size: {
                                height: height / videoObjectSize.height,
                                width: width / videoObjectSize.width,
                              },
                            });
                          }}>
                          {getElementObject(element, isEditMode, isVisible)}
                        </Resizable>
                      </Deleteable>
                    </Rotateable>
                  </Draggable>
                </div>
              );
            } else {
              return (
                <div style={{ opacity: isVisible ? 1 : 0 }} key={element.id}>
                  <div
                    style={{
                      position: 'absolute',
                      top: `${element.position.y * videoObjectSize.height}px`,
                      left: `${element.position.x * videoObjectSize.width}px`,
                      height: `${element.size.height * videoObjectSize.height}px`,
                      width: `${element.size.width * videoObjectSize.width}px`,
                    }}>
                    {getElementObject(element, isEditMode, isVisible)}
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}
      <div className={`gradient-bottom ${!isVideoControlsVisible && 'hidden'}`} />
      {totalDuration && (
        <div className={`video-controls ${!isVideoControlsVisible && 'hidden'}`}>
          <Scrubber ref={scrubberRef} max={totalDuration} value={currentTime} onInput={onUpdateTime} />
          <div className={'options'}>
            <button className={'play-button'} onClick={onTogglePlay}>
              <img src={isPlaying ? '/icons/pause.svg' : '/icons/play.svg'} alt='' />
            </button>
            <div className={'time'}>{`${Math.round(currentTime)} / ${Math.round(totalDuration)}s`}</div>
            <button className={'fullscreen-button'} onClick={onToggleFullScreen}>
              {isFullScreen ? <img src='/icons/fullscreen-exit.svg' alt='' /> : <img src='/icons/fullscreen.svg' alt='' />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
